import React, { FC, KeyboardEventHandler, MouseEventHandler } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar as faStarSolid } from '@fortawesome/free-solid-svg-icons';
import { faStar as faStarRegular } from '@fortawesome/free-regular-svg-icons';
import classNames from 'classnames';
import { Modal, Form, Input, Spin } from 'antd';
import { Button } from '@plataforma-xvia/design-system-es';
import { DoneAll } from '@mui/icons-material';
import styles from './catalog-rating.module.css';
import { useCatalogRating } from './use-catalog-rating';
import { formatRatingValue } from '../../utils/rating';

export interface CatalogRatingProps {
  serviceId: string;
  serviceName: string;
  averageRatings?: { count: number; average: number };
  className?: string;
}

export const CatalogRating: FC<CatalogRatingProps> = ({
  serviceId,
  serviceName,
  averageRatings,
  className,
}) => {
  const {
    showModal,
    isSubmitting,
    form,
    commentLength,
    currentRating,
    isInCooldown,
    userRating,
    isLoading,
    feedback,

    handleStarClick,
    handleStarMouseEnter,
    handleStarMouseLeave,
    handleStarKeyDown,
    handleSubmitButtonClick,
    handleModalSubmit,
    handleModalCancel,
    handleCommentChange,
    COMMENT_RULES,
  } = useCatalogRating({ serviceId, serviceName });

  const containerClassName = classNames({
    [styles['catalog-rating']]: true,
    [className || '']: !!className,
    [styles['catalog-rating--cooldown']]: isInCooldown,
    [styles['catalog-rating--rated']]: !!userRating,
  });

  const promptText = userRating
    ? 'Obrigado pela avaliação!'
    : 'Esse conteúdo foi útil para você? Faça uma avaliação!';

  const createStarClickHandler = (
    starRating: number
  ): MouseEventHandler<SVGSVGElement> => {
    return () => {
      if (!isInCooldown && !userRating) {
        handleStarClick(starRating);
      }
    };
  };

  const createStarMouseEnterHandler = (
    starRating: number
  ): MouseEventHandler<SVGSVGElement> => {
    return () => {
      if (!isInCooldown && !userRating) {
        handleStarMouseEnter(starRating);
      }
    };
  };

  const createStarKeyDownHandler = (
    starRating: number
  ): KeyboardEventHandler<SVGSVGElement> => {
    return (evt) => {
      if (!isInCooldown && !userRating) {
        handleStarKeyDown(starRating, evt);
      }
    };
  };

  if (isLoading) {
    return (
      <div
        className={containerClassName}
        data-testid="catalog-rating-container"
      >
        <span
          className={styles['catalog-rating__prompt']}
          data-testid="catalog-rating-title"
        >
          {promptText}
        </span>
        <div
          className={styles['catalog-rating__stars']}
          data-testid="catalog-rating-stars"
        >
          <Spin size="small" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className={containerClassName}
        data-testid="catalog-rating-container"
      >
        <hr className={styles['catalog-rating__divider']} />
        <span
          className={styles['catalog-rating__prompt']}
          data-testid="catalog-rating-title"
        >
          {promptText}
        </span>
        <div className={styles['catalog-rating__columns']}>
          <div className={styles['catalog-rating__col']}>
            <span className={styles['catalog-rating__col-label']}>
              Avaliação do serviço
            </span>
            <div
              className={styles['catalog-rating__stars']}
              data-testid="catalog-rating-stars"
              role="radiogroup"
              aria-label={`Avaliação do serviço ${serviceName}`}
            >
              {Array.from(Array(5).keys()).map((i) => (
                <FontAwesomeIcon
                  key={i}
                  className={classNames({
                    [styles['catalog-rating__star']]: true,
                    [styles['catalog-rating__star--filled']]: i < currentRating,
                    [styles['catalog-rating__star--hover']]:
                      i < currentRating && !isInCooldown && !userRating,
                    [styles['catalog-rating__star--disabled']]:
                      isInCooldown || !!userRating,
                  })}
                  icon={i < currentRating ? faStarSolid : faStarRegular}
                  onClick={createStarClickHandler(i + 1)}
                  onMouseEnter={createStarMouseEnterHandler(i + 1)}
                  onMouseLeave={handleStarMouseLeave}
                  onKeyDown={createStarKeyDownHandler(i + 1)}
                  tabIndex={isInCooldown || !!userRating ? -1 : 0}
                  aria-pressed={i < currentRating}
                  aria-label={
                    userRating
                      ? `Você avaliou ${serviceName} com ${currentRating} estrelas`
                      : `Avaliar ${serviceName} com ${i + 1} estrelas`
                  }
                  aria-disabled={isInCooldown || !!userRating}
                  data-testid={`catalog-rating-star-${i + 1}`}
                />
              ))}
            </div>
          </div>
          {averageRatings && (
            <div className={styles['catalog-rating__col']}>
              <span className={styles['catalog-rating__col-label']}>
                Nota geral do serviço
              </span>
              <strong
                className={styles['catalog-rating__average-value']}
                data-testid="catalog-rating-average"
              >
                {formatRatingValue(averageRatings.average)}
              </strong>
            </div>
          )}
        </div>
        <div className={styles['catalog-rating__actions']}>
          <Button
            variant="default"
            size="small"
            label={userRating ? 'Sua avaliação foi enviada com sucesso' : 'Enviar avaliação'}
            iconEnd={<DoneAll fontSize="small" />}
            onClick={handleSubmitButtonClick}
            disabled={!!userRating || isInCooldown || isSubmitting}
          />
        </div>
        {feedback && (
          <span
            className={classNames(styles['catalog-rating__feedback'], {
              [styles['catalog-rating__feedback--success']]:
                feedback.type === 'success',
              [styles['catalog-rating__feedback--error']]:
                feedback.type === 'error',
              [styles['catalog-rating__feedback--info']]:
                feedback.type === 'info',
            })}
          >
            {feedback.message}
          </span>
        )}
      </div>

      <Modal
        className={styles['catalog-rating__modal']}
        width={600}
        title={
          <div className={styles['catalog-rating__header']}>
            <span className={styles['catalog-rating__header-icon']} aria-hidden>
              <svg
                width="16"
                height="16"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.248657 19.7514H4.31116L16.2928 7.7697L12.2303 3.7072L0.248657 15.6889V19.7514ZM2.41532 16.588L12.2303 6.77303L13.227 7.7697L3.41199 17.5847H2.41532V16.588Z"
                  fill="#FDFDFD"
                />
                <path
                  d="M16.8995 0.565532C16.477 0.143032 15.7945 0.143032 15.372 0.565532L13.3895 2.54803L17.452 6.61053L19.4345 4.62803C19.857 4.20553 19.857 3.52303 19.4345 3.10053L16.8995 0.565532Z"
                  fill="#FDFDFD"
                />
              </svg>
            </span>
            <span className={styles['catalog-rating__header-title']}>
              Deixe seu comentário
            </span>
          </div>
        }
        closeIcon={
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-label="Fechar"
          >
            <path
              d="M15.5833 1.94419L14.0558 0.416687L7.99996 6.47252L1.94413 0.416687L0.416626 1.94419L6.47246 8.00002L0.416626 14.0559L1.94413 15.5834L7.99996 9.52752L14.0558 15.5834L15.5833 14.0559L9.52746 8.00002L15.5833 1.94419Z"
              fill="#FDFDFD"
            />
          </svg>
        }
        open={showModal}
        onCancel={handleModalCancel}
        footer={null}
        destroyOnClose
        data-testid="catalog-rating-modal"
      >
        <div
          className={styles['catalog-rating__modal-content']}
          data-testid="catalog-rating-modal-content"
        >
          <div
            className={styles['catalog-rating__modal-rating']}
            data-testid="catalog-rating-modal-rating"
          >
            <span className={styles['catalog-rating__label']}>
              Sua avaliação:
            </span>
            <div className={styles['catalog-rating__modal-stars']}>
              {Array.from(Array(5).keys()).map((i) => (
                <FontAwesomeIcon
                  key={i}
                  className={classNames({
                    [styles['catalog-rating__modal-star']]: true,
                    [styles['catalog-rating__modal-star--filled']]:
                      i < currentRating,
                  })}
                  icon={'fa-solid fa-star' as any}
                  data-testid={`catalog-rating-modal-star-${i + 1}`}
                />
              ))}
            </div>
          </div>

          <div className={styles['catalog-rating__separator']} />

          <Form
            form={form}
            layout="vertical"
            className={styles['catalog-rating__modal-form']}
            requiredMark={false}
            data-testid="catalog-rating-modal-form"
          >
            <Form.Item
              label={
                <span className={styles['catalog-rating__form-label']}>
                  Comentário
                  {currentRating >= 1 && currentRating <= 3 && (
                    <span className={styles['required']}> *</span>
                  )}
                </span>
              }
              name="comment"
              rules={[
                { required: true, message: 'O comentário é obrigatório.' },
                {
                  validator: (_, value) => {
                    if (!value || value.length === 0) {
                      return Promise.resolve();
                    }
                    if (value.length < COMMENT_RULES.minLength) {
                      return Promise.reject(
                        new Error(
                          'O comentário deve ter pelo menos 15 caracteres!'
                        )
                      );
                    }
                    if (value.length > COMMENT_RULES.maxLength) {
                      return Promise.reject(
                        new Error(
                          'O comentário deve ter no máximo 500 caracteres!'
                        )
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              data-testid="catalog-rating-comment-field"
            >
              <Input.TextArea
                rows={4}
                maxLength={COMMENT_RULES.maxLength}
                placeholder="Conte-nos sua experiência com este serviço..."
                onChange={(e) => handleCommentChange(e.target.value)}
                autoFocus
                data-testid="catalog-rating-comment-input"
              />
            </Form.Item>
            {commentLength >= 490 && commentLength < 500 && (
              <div className={styles['catalog-rating__char-counter']}>
                {commentLength}/500
              </div>
            )}
            {commentLength === 500 && (
              <div className={styles['catalog-rating__limit-reached']}>
                Máximo de 500 caracteres alcançado
              </div>
            )}
          </Form>
          <div className={styles['catalog-rating__actions']}>
            <button
              type="button"
              className={styles['catalog-rating__cancel-button']}
              onClick={handleModalCancel}
            >
              <span className={styles['catalog-rating__cancel-content']}>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 10 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden
                >
                  <path
                    d="M9.66671 1.27331L8.72671 0.333313L5.00004 4.05998L1.27337 0.333313L0.333374 1.27331L4.06004 4.99998L0.333374 8.72665L1.27337 9.66665L5.00004 5.93998L8.72671 9.66665L9.66671 8.72665L5.94004 4.99998L9.66671 1.27331Z"
                    fill="#6C7393"
                  />
                </svg>
                <span>Cancelar</span>
              </span>
            </button>
            <button
              type="button"
              className={styles['catalog-rating__ok-button']}
              onClick={handleModalSubmit}
              disabled={isSubmitting}
            >
              <span className={styles['catalog-rating__ok-content']}>
                <span>Avaliar</span>
                <svg
                  className={styles['catalog-rating__ok-icon']}
                  width="16"
                  height="16"
                  viewBox="0 0 21 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden
                >
                  <path d="M15.4708 1.58748L14.2958 0.412476L9.0125 5.69581L10.1875 6.87081L15.4708 1.58748ZM19.0042 0.412476L10.1875 9.22914L6.70417 5.75414L5.52917 6.92914L10.1875 11.5875L20.1875 1.58748L19.0042 0.412476ZM0.8125 6.92914L5.47083 11.5875L6.64583 10.4125L1.99583 5.75414L0.8125 6.92914Z" />
                </svg>
              </span>
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};
